<script setup lang="ts">
import { computed } from 'vue';
import DocumentBasicInfoLayout from '@/components/common/adp-document/basic-info/document-basic-info-layout.vue';
import DocumentMediaName from '@/components/common/adp-document/basic-info/document-media-name.vue';
import { Patent } from '@/types';

interface Props {
  adpDocument: Patent;
  size?: 'xm' | 'm' | 'l';
  imageVariant?: 'banner' | 'thumbnail';
  isNarrow?: boolean;
  isRelatedArticle?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: 'xm',
  imageVariant: 'thumbnail',
  isNarrow: false,
  isRelatedArticle: false,
});

const emit = defineEmits<{
  'article-link-clicked': [clickedMainTitle?: boolean];
  'article-link-context-menu': [event: Event];
}>();

const publicationOffice = computed(() =>
  props.adpDocument.publication_office?.toUpperCase(),
);
</script>

<template>
  <DocumentBasicInfoLayout
    :adp-document="adpDocument"
    :size="size"
    :image-variant="imageVariant"
    :is-narrow="isNarrow"
    :is-related-article="isRelatedArticle"
    @article-link-clicked="emit('article-link-clicked', $event)"
    @article-link-context-menu="emit('article-link-context-menu', $event)"
  >
    <template #publisher>
      <DocumentMediaName :publisher="adpDocument.applicant" />
    </template>
    <template #dateExtra>
      <div v-if="publicationOffice" class="c-text-regular--s">
        ・{{ publicationOffice }}
      </div>
      <div v-if="adpDocument.patent_identifier" class="c-text-regular--s">
        ・{{ adpDocument.patent_identifier }}
      </div>
    </template>
    <template #bottom>
      <div
        v-if="adpDocument.issue"
        class="patent-issue c-text-regular--s"
        :class="{
          'patent-issue-m': size === 'xm' || size === 'm',
          'patent-issue-l': size === 'l',
        }"
      >
        {{ adpDocument.issue }}
      </div>
    </template>
  </DocumentBasicInfoLayout>
</template>

<style lang="scss" scoped>
.patent-issue {
  color: $color-gray800;

  &.patent-issue-m {
    padding: 12px 0;
  }
  &.patent-issue-l {
    padding: 0 16px 12px 16px;
  }
}
</style>
